import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ticketService } from "@/services/ticketService";
import { userService } from "@/services/userService";
import { attachmentService } from "@/services/attachmentService";
import { ticketLinkService } from "@/services/ticketLinkService";
import { watcherService } from "@/services/watcherService";
import { timeEntryService } from "@/services/timeEntryService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { StatusBadge, PriorityBadge, SLABadge } from "@/utils/constants";
import { formatDate } from "@/utils/formatters";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Paperclip, Activity, Trash2, Save, Timer, X } from "lucide-react";

import { CommentsSection } from "@/components/tickets/CommentsSection";
import { AttachmentsSection } from "@/components/tickets/AttachmentsSection";
import { ActivitySection } from "@/components/tickets/ActivitySection";
import { TimeTrackingSection } from "@/components/tickets/TimeTrackingSection";
import { DetailsPanel, SLAPanel, StatusTransitionPanel, WatchersPanel } from "@/components/tickets/DetailPanels";
import { LinkedTicketsPanel } from "@/components/tickets/LinkedTicketsPanel";

const VALID_TRANSITIONS = {
  open: ["assigned", "in_progress"], assigned: ["in_progress", "on_hold"],
  in_progress: ["on_hold", "resolved"], on_hold: ["in_progress", "assigned"],
  resolved: ["closed", "reopened"], closed: ["reopened"], reopened: ["assigned", "in_progress"],
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editPriority, setEditPriority] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  const [editAssignee, setEditAssignee] = useState(null);
  const [editStatus, setEditStatus] = useState(null);

  const [links, setLinks] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [isWatching, setIsWatching] = useState(false);
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeSummary, setTimeSummary] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchData = useCallback(async () => {
    try {
      const [tRes, cRes, aRes, attRes] = await Promise.all([
        ticketService.get(id), ticketService.getComments(id),
        ticketService.getActivity(id), attachmentService.list(id),
      ]);
      setTicket(tRes.data); setComments(cRes.data); setActivities(aRes.data); setAttachments(attRes.data);
    } catch { toast.error("Failed to load ticket"); navigate("/tickets"); } finally { setLoading(false); }
  }, [id, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const refreshActivity = useCallback(async () => {
    const aRes = await ticketService.getActivity(id);
    setActivities(aRes.data);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- userService is a stable import
  useEffect(() => { if (user?.role !== "end_user") { userService.listAgents().then(({ data }) => setAgents(data)).catch(() => {}); } }, [user?.role]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchLinks = useCallback(() => { ticketLinkService.list(id).then(({ data }) => setLinks(data)).catch(() => {}); }, [id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchWatchers = useCallback(() => { watcherService.list(id).then(({ data }) => { setWatchers(data); setIsWatching(data.some((w) => w.user_id === user?.id)); }).catch(() => {}); }, [id, user?.id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchTimeEntries = useCallback(() => {
    timeEntryService.list(id).then(({ data }) => setTimeEntries(data)).catch(() => {});
    timeEntryService.summary(id).then(({ data }) => setTimeSummary(data)).catch(() => {});
  }, [id]);

  useEffect(() => { fetchLinks(); fetchWatchers(); fetchTimeEntries(); }, [fetchLinks, fetchWatchers, fetchTimeEntries]);
  useEffect(() => { if (ticket) { setEditPriority(null); setEditCategory(null); setEditAssignee(null); } }, [ticket]);

  const hasPendingChanges = useMemo(() => {
    if (!ticket) return false;
    if (editPriority !== null && editPriority !== ticket.priority) return true;
    if (editCategory !== null && editCategory !== ticket.category) return true;
    const currentAssignee = ticket.assigned_to || "";
    if (editAssignee !== null && editAssignee !== currentAssignee) return true;
    return false;
  }, [ticket, editPriority, editCategory, editAssignee]);

  const handleStatusChange = (newStatus) => {
    setEditStatus(editStatus === newStatus ? null : newStatus);
  };

  const handleSaveChanges = async () => {
    if (!hasPendingChanges || saving) return;
    setSaving(true);
    try {
      const fieldUpdate = {};
      if (editPriority !== null && editPriority !== ticket.priority) fieldUpdate.priority = editPriority;
      if (editCategory !== null && editCategory !== ticket.category) fieldUpdate.category = editCategory;
      if (editAssignee !== null) {
        const currentAssignee = ticket.assigned_to || "";
        if (editAssignee !== currentAssignee) fieldUpdate.assigned_to = editAssignee;
      }
      if (Object.keys(fieldUpdate).length > 0) {
        const { data } = await ticketService.update(id, fieldUpdate);
        setTicket(data);
      }
      await refreshActivity();
      toast.success("Ticket updated successfully");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const handleDiscardChanges = () => { setEditPriority(null); setEditCategory(null); setEditAssignee(null); };

  const handleDelete = async () => {
    if (!window.confirm("Delete this ticket permanently?")) return;
    try { await ticketService.delete(id); toast.success("Ticket deleted"); navigate("/tickets"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-8 w-64 bg-zinc-200 rounded-sm" /><div className="h-48 bg-zinc-200 rounded-sm" /></div></div>;
  if (!ticket) return null;

  const currentStatus = editStatus || ticket.status;
  const transitions = VALID_TRANSITIONS[ticket.status] || [];
  const perm = ticket.permission || "read";
  const canModify = perm !== "read";
  const canEditFields = perm === "full" || perm === "work";
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const slaStatus = ticket.sla_status;

  const allowedTransitions = transitions.filter((s) => {
    if (perm === "full") return true;
    if (perm === "work") return s !== "closed";
    if (perm === "close") return s === "closed" || s === "reopened";
    return false;
  });

  return (
    <div className="p-6 space-y-4" data-testid="ticket-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/tickets")} className="rounded-sm gap-1" data-testid="back-to-tickets"><ArrowLeft size={15} /> Back</Button>
        <span className="font-mono text-sm text-[#52525B]">{ticket.ticket_number}</span>
        <StatusBadge status={currentStatus} /><PriorityBadge priority={editPriority || ticket.priority} />
        {slaStatus && (
          <div className="flex gap-1" data-testid="sla-badges-header">
            <SLABadge type="response" status={slaStatus.response} />
            <SLABadge type="resolution" status={slaStatus.resolution} />
          </div>
        )}
        {canDelete && (<Button variant="ghost" size="sm" onClick={handleDelete} className="ml-auto rounded-sm text-red-600 hover:bg-red-50 gap-1" data-testid="delete-ticket-btn"><Trash2 size={14} /> Delete</Button>)}
        {perm === "read" && (<span className="ml-auto text-xs text-[#52525B] bg-[#F4F4F5] px-2.5 py-1 rounded-sm" data-testid="read-only-badge">View Only</span>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[#E4E4E7] shadow-none rounded-sm">
            <CardContent className="p-6">
              <h2 className="font-heading text-xl font-bold text-[#09090B]" data-testid="ticket-title">{ticket.title}</h2>
              <p className="text-sm text-[#52525B] mt-1">Created by <span className="font-medium">{ticket.created_by_name}</span> on {formatDate(ticket.created_at)}</p>
              <Separator className="my-4" />
              <div className="prose prose-sm max-w-none text-[#09090B]" data-testid="ticket-description"><p className="whitespace-pre-wrap text-sm leading-relaxed">{ticket.description}</p></div>
            </CardContent>
          </Card>

          <Tabs defaultValue="comments" className="w-full">
            <TabsList className="rounded-sm bg-[#F4F4F5] h-9">
              <TabsTrigger value="comments" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-comments"><MessageSquare size={14} /> Comments ({comments.length})</TabsTrigger>
              <TabsTrigger value="attachments" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-attachments"><Paperclip size={14} /> Files ({attachments.length})</TabsTrigger>
              <TabsTrigger value="activity" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-activity"><Activity size={14} /> Activity ({activities.length})</TabsTrigger>
              <TabsTrigger value="time" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-time"><Timer size={14} /> Time</TabsTrigger>
            </TabsList>

            <TabsContent value="comments" className="mt-3 space-y-3">
              <CommentsSection comments={comments} setComments={setComments} setAttachments={setAttachments}
                canModify={canModify} user={user} ticketId={id}
                editStatus={editStatus} setEditStatus={setEditStatus} setTicket={setTicket}
                refreshActivity={refreshActivity} />
            </TabsContent>
            <TabsContent value="attachments" className="mt-3 space-y-3">
              <AttachmentsSection attachments={attachments} setAttachments={setAttachments} canModify={canModify} user={user} ticketId={id} fileInputRef={fileInputRef} refreshActivity={refreshActivity} />
            </TabsContent>
            <TabsContent value="activity" className="mt-3">
              <ActivitySection activities={activities} />
            </TabsContent>
            <TabsContent value="time" className="mt-3 space-y-3">
              <TimeTrackingSection timeEntries={timeEntries} timeSummary={timeSummary} user={user} ticketId={id} fetchTimeEntries={fetchTimeEntries} refreshActivity={refreshActivity} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <DetailsPanel ticket={ticket} agents={agents} currentStatus={currentStatus}
            canEditFields={canEditFields}
            editPriority={editPriority} setEditPriority={setEditPriority}
            editCategory={editCategory} setEditCategory={setEditCategory}
            editAssignee={editAssignee} setEditAssignee={setEditAssignee} />

          {slaStatus && <SLAPanel slaStatus={slaStatus} ticket={ticket} />}

          {allowedTransitions.length > 0 && canModify && (
            <StatusTransitionPanel transitions={allowedTransitions} editStatus={editStatus} onStatusChange={handleStatusChange} />
          )}

          {hasPendingChanges && (
            <Card className="border-[#003DA5] bg-[#F0F4FF] shadow-none rounded-sm" data-testid="pending-changes-bar">
              <CardContent className="p-3 flex items-center gap-2">
                <span className="text-xs font-medium text-[#003DA5] flex-1">Unsaved changes</span>
                <Button variant="ghost" size="sm" onClick={handleDiscardChanges} className="rounded-sm text-xs h-7" data-testid="discard-changes-btn">Discard</Button>
                <Button size="sm" onClick={handleSaveChanges} disabled={saving} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white gap-1.5 text-xs h-7" data-testid="save-changes-btn">
                  <Save size={12} /> {saving ? "Saving..." : "Update Ticket"}
                </Button>
              </CardContent>
            </Card>
          )}

          <WatchersPanel watchers={watchers} isWatching={isWatching} onToggleWatch={async () => {
            try {
              if (isWatching) { await watcherService.unwatch(id); toast.success("Unwatched"); }
              else { await watcherService.watch(id); toast.success("Now watching"); }
              fetchWatchers();
            } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
          }} />

          <LinkedTicketsPanel links={links} user={user} navigate={navigate} ticketId={id}
            fetchLinks={fetchLinks} refreshActivity={refreshActivity} />
        </div>
      </div>
    </div>
  );
}
